import { useEffect, useState } from "react";
import { AlertTriangle, ClipboardList } from "lucide-react";
import { api } from "@/lib/api";
import { SIGNATURE_STATUS_PANEL } from "@/constants/testIds";

const STATUS_META = {
  generated: { label: "Generado", className: "bg-charcoal/10 text-charcoal/60" },
  enviado_para_revision: { label: "Enviado a revisión", className: "bg-petroleum/10 text-petroleum" },
  pendiente_firma_fisica: { label: "Pendiente de firma física", className: "bg-amber-50 text-amber-700" },
  firmado_fisico_respaldo_digital: { label: "Firmado físico", className: "bg-emerald-50 text-emerald-700" },
  confirmado_manual_admin: { label: "Confirmado manualmente por RH", className: "bg-emerald-50 text-emerald-700" },
};

export function SignatureStatusPanel() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/signable-documents/status-overview").then(({ data }) => setRows(data));
  }, []);

  if (!rows) return null;
  const reminders = rows.filter((r) => r.needs_reminder);
  const summaryByTitle = rows
    .filter((r) => r.status !== "reemplazado")
    .reduce((acc, r) => {
      acc[r.title] = acc[r.title] || { total: 0, signed: 0 };
      acc[r.title].total += 1;
      if (r.status === "firmado_fisico_respaldo_digital" || r.status === "confirmado_manual_admin") acc[r.title].signed += 1;
      return acc;
    }, {});

  return (
    <div data-testid={SIGNATURE_STATUS_PANEL.section} className="bg-card border border-petroleum/10 rounded-2xl p-5 mb-6">
      <p className="text-sm font-semibold text-charcoal mb-3 flex items-center gap-1.5">
        <ClipboardList className="w-4 h-4 text-terracotta" strokeWidth={1.5} /> Estatus de firmas (todas las personas)
      </p>

      {Object.keys(summaryByTitle).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {Object.entries(summaryByTitle).map(([title, { total, signed }]) => (
            <span key={title} data-testid={SIGNATURE_STATUS_PANEL.summaryBadge} className="text-xs font-medium bg-petroleum/5 text-petroleum px-3 py-1.5 rounded-full">
              {signed} de {total} firmaron su {title}
            </span>
          ))}
        </div>
      )}

      {reminders.length > 0 && (
        <div data-testid={SIGNATURE_STATUS_PANEL.reminderBanner} className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{reminders.length} documento{reminders.length > 1 ? "s" : ""} lleva{reminders.length > 1 ? "n" : ""} 3 días o más pendiente{reminders.length > 1 ? "s" : ""} de firma física. Da seguimiento con RH.</span>
        </div>
      )}

      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {rows.map((r) => (
          <div key={r.id} data-testid={SIGNATURE_STATUS_PANEL.row} className="flex items-center justify-between gap-2 text-sm py-1.5 border-b border-petroleum/5 last:border-0">
            <div className="min-w-0">
              <p className="text-charcoal truncate">{r.employee_name} <span className="text-charcoal/40">·</span> {r.title}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {r.needs_reminder && (
                <span data-testid={SIGNATURE_STATUS_PANEL.reminderBadge} className="text-xs font-medium text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> {r.days_pending}d
                </span>
              )}
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_META[r.status]?.className || "bg-charcoal/10 text-charcoal/60"}`}>
                {STATUS_META[r.status]?.label || r.status}
              </span>
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-charcoal/50">Aún no se han generado documentos de firma.</p>}
      </div>
    </div>
  );
}
